/**
 * SCR-001 — Login screen.
 * Public route. Already-logged-in users redirect to their dashboard.
 * better-auth signIn.email → redirect by role on success.
 * Shows unverified-email banner with 240s resend cooldown per spec.
 */
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authClient } from '@client/lib/auth-client';

const searchSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute('/login')({
  validateSearch: zodValidator(searchSchema),
  beforeLoad: async ({ navigate }) => {
    const { data: session } = await authClient.getSession();
    if (session?.user) {
      const role = (session.user as { role?: string }).role ?? '';
      const dest = roleDestination(role);
      throw navigate({ to: dest, replace: true });
    }
  },
  component: LoginPage,
});

function roleDestination(role: string): string {
  if (role === 'admin' || role === 'manager') return '/admin';
  if (role === 'teacher') return '/teacher';
  return '/portal';
}

const loginSchema = z.object({
  email: z.string().email('Email không hợp lệ'),
  password: z.string().min(1, 'Vui lòng nhập mật khẩu'),
});

type LoginForm = z.infer<typeof loginSchema>;

const COOLDOWN_SECS = 240;

function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/login' });
  const redirectTo = getInternalRedirect(search.redirect);

  const [showPw, setShowPw] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showUnverified, setShowUnverified] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [resendEmail, setResendEmail] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<LoginForm>({ resolver: zodResolver(loginSchema) });

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  async function onSubmit(values: LoginForm) {
    setApiError(null);
    setShowUnverified(false);

    const { error, data } = await authClient.signIn.email({
      email: values.email.trim().toLowerCase(),
      password: values.password,
    });

    if (error) {
      const msg = (error as { message?: string }).message ?? '';
      if (msg.toLowerCase().includes('verif') || msg.toLowerCase().includes('email')) {
        setShowUnverified(true);
        setResendEmail(values.email.trim().toLowerCase());
      } else if (msg.includes('429') || msg.toLowerCase().includes('rate')) {
        setApiError('Quá nhiều lần thử, vui lòng thử lại sau.');
      } else if (msg.toLowerCase().includes('inactive') || msg.toLowerCase().includes('disabled')) {
        setApiError('Tài khoản đã ngừng hoạt động.');
      } else if (msg.toLowerCase().includes('network') || msg.toLowerCase().includes('fetch')) {
        setApiError('Không kết nối được máy chủ, thử lại sau.');
      } else {
        setApiError('Sai email hoặc mật khẩu.');
      }
      return;
    }

    if (data?.user) {
      const role = (data.user as { role?: string }).role ?? '';
      const dest = redirectTo ?? roleDestination(role);
      void navigate({ to: dest, replace: true });
    }
  }

  async function handleResend() {
    if (cooldown > 0) return;
    const email = resendEmail || getValues('email').trim().toLowerCase();
    try {
      await authClient.sendVerificationEmail({ email });
      toast.success('Đã gửi lại email xác thực (nếu tài khoản hợp lệ).');
    } catch {
      toast.error('Vui lòng thử lại sau.');
    }
    startCooldown();
  }

  function startCooldown() {
    setCooldown(COOLDOWN_SECS);
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-[400px]">
        {/* Logo */}
        <div className="flex flex-col items-center gap-2 mb-8">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary shadow-md">
            <span className="text-lg font-bold text-primary-foreground">CLC</span>
          </div>
          <h2 className="text-xl font-semibold text-foreground">Đăng nhập hệ thống</h2>
        </div>

        {/* Card */}
        <div className="rounded-lg border border-border bg-card p-6 shadow-sm flex flex-col gap-4">
          {/* API error */}
          {apiError && (
            <Alert variant="destructive">
              <AlertDescription>{apiError}</AlertDescription>
            </Alert>
          )}

          {/* Unverified email banner */}
          {showUnverified && (
            <Alert className="border-brand-orange-300 bg-brand-orange-50 text-brand-orange-900">
              <AlertDescription className="flex flex-col gap-2">
                <span>
                  Tài khoản chưa được xác thực email. Vui lòng kiểm tra hộp thư
                  và xác thực để đăng nhập.
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="self-start border-brand-orange-400"
                  disabled={cooldown > 0 || isSubmitting}
                  onClick={handleResend}
                >
                  {cooldown > 0
                    ? `Gửi lại sau ${cooldown}s`
                    : 'Gửi lại email xác thực'}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
            {/* Email */}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                autoFocus
                autoComplete="email"
                placeholder="name@clc.edu.vn"
                className="h-11"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">Mật khẩu</Label>
                <a
                  href="/forgot-password"
                  className="text-xs text-primary hover:text-primary/80 transition-colors"
                >
                  Quên mật khẩu?
                </a>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="h-11 pr-10"
                  {...register('password')}
                />
                <button
                  type="button"
                  aria-label={showPw ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowPw((v) => !v)}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-destructive">{errors.password.message}</p>
              )}
            </div>

            <Button
              type="submit"
              className="h-11 w-full mt-1"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Đang đăng nhập…
                </span>
              ) : (
                'Đăng nhập'
              )}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          CLC Education LMS · v1.0
        </p>
      </div>
    </div>
  );
}

/** Only accept internal paths starting with / to prevent open redirect */
function getInternalRedirect(redirect?: string): string | null {
  if (!redirect) return null;
  try {
    const url = new URL(redirect, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return url.pathname + url.search;
  } catch {
    if (redirect.startsWith('/')) return redirect;
    return null;
  }
}
